import { toast } from "@/components/Toast";
import { CoinsStatusText } from "@/modules/editor/components/CoinsStatusText";
import { FreeTaskMessage } from "@/modules/editor/components/FreeTaskMessage";
import { GenerateCostText } from "@/modules/editor/components/GenerateCostText";
import { useGenerateCost } from "@/modules/editor/hooks/useGenerateCost";
import { GeneratePriceProps } from "@/modules/editor/utils/generateCost";
import { tasksStore } from "@/stores/tasks";
import { WithClassName } from "@/types/base";
import events, { MessageType } from "@/utils/events";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import { isAuthUser, isNotVipAuthUser, isVip } from "@/utils/status";
import { Button, ButtonProps, Center, chakra, ChakraProps } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { PropsWithChildren } from "react";

type BizGeneratButtonProps = {
  size?: { w: number; h: number };
  calcPriceProps?: Omit<GeneratePriceProps, "size">;
  generateCount?: number;
  onSubmit?: (data: { cost: number; affordable: boolean }) => void | Promise<void>;
  checkAffordable?: boolean;
  showCost?: boolean;
  useCoin?: "total" | "payed";
  checkBusyConfig?: {
    checkVip?: boolean;
    checkAll?: boolean;
    busyAlias?: string[] | string;
  };
  sticky?: boolean;
  buttonProps?: Omit<ButtonProps, "size" | "onClick">;
  cost?: number;
};

const stickyStyles: ChakraProps = {
  pos: "sticky",
  bottom: "24px",
  zIndex: 2,
  _after: {
    content: "''",
    zIndex: -1,
    w: "100%",
    h: "200%",
    pos: "absolute",
    left: 0,
    top: "-16px",
    bg: "#323232",
  },
  flexDir: "column",
};
const BizGeneratButton: React.FC<PropsWithChildren<WithClassName<BizGeneratButtonProps>>> = ({
  size,
  cost,
  calcPriceProps,
  generateCount = 1,
  onSubmit,
  checkAffordable = true,
  children,
  showCost = isNotVipAuthUser(),
  checkBusyConfig,
  className,
  useCoin = "total",
  sticky,
  buttonProps,
}) => {
  const { translate } = useTranslate();
  const { cost: _cost, affordable } = useGenerateCost({
    generateCount,
    size,
    calcPriceProps,
    useCoin,
    cost,
  });

  function handleSubmit() {
    if (checkBusyConfig?.busyAlias) {
      const { busyAlias } = checkBusyConfig;
      const isBusy = Array.isArray(busyAlias)
        ? busyAlias.some((alias) => tasksStore.busyNodes.has(alias))
        : tasksStore.busyNodes.has(busyAlias);
      if (isBusy) {
        if (checkBusyConfig.checkAll) {
          toast({
            status: "info",
            title: translate(Editor_Words["already-in-queue-message"]),
          });
          return;
        }

        if (checkBusyConfig.checkVip && !isVip()) {
          if (!isAuthUser()) {
            toast({
              status: "info",
              title: translate(Editor_Words["already-in-queue-message"]),
            });
            return;
          } else {
            toast({
              status: "info",
              title: <FreeTaskMessage />,
            });
            return;
          }
        }
      }
    }
    if (checkAffordable && !affordable) {
      events.emit(MessageType.HEART_COIN);
      return;
    }

    onSubmit?.({ cost: _cost, affordable });
  }
  return (
    <Center mt="16px" flexDirection="column" className={className} {...(sticky && stickyStyles)}>
      <CoinsStatusText showItem={useCoin} />
      <Button w="100%" onClick={handleSubmit} {...buttonProps}>
        {children}
        <GenerateCostText cost={_cost} show={showCost} />
      </Button>
    </Center>
  );
};

export default chakra(observer(BizGeneratButton));
